import type { ICellRendererParams } from 'ag-grid-community';
import clsx from 'clsx';
import { Suspense, lazy, memo, useMemo } from 'react';

import { useStatisticFilters } from '../../hooks/filters';
import { useStatisticOptions } from '../../store';

import { useWindowSize } from '@hooks/useWindowSize';
import type { Player, Team } from '@shared-types/match.types';

const TeamIdentifier = lazy(() => import('@shared/Identifier/TeamIdentifier'));
const PlayerIdentifier = lazy(() => import('@shared/Identifier/PlayerIdentifier'));

export const Identifier = ({ value, data }: ICellRendererParams<{ team: Team }, Team | Player>) => {
  const {
    options: { statType, isHistorySection },
  } = useStatisticOptions();
  const { filters } = useStatisticFilters();
  const { isMobile } = useWindowSize();

  const props = useMemo(
    () => ({
      enableLink: statType === 'team' || !isHistorySection,
      linkSubPage: isHistorySection ? '' : 'statistics',
      phase: filters?.phase,
      wrapperClass: 'pk-w--100',
      primarySize: isMobile ? 14 : 16,
    }),
    [filters?.phase, isMobile, isHistorySection, statType],
  );

  return (
    <Suspense>
      {statType === 'team' ? (
        <TeamIdentifier
          team={value}
          {...props}
          badgeSize={40}
          badgeClassName={clsx(isMobile && 'pk-mr--xs2')}
          isHistorical={isHistorySection}
        />
      ) : (
        <PlayerIdentifier
          player={value}
          team={data.team}
          {...props}
          avatarSize={40}
          avatarClassName={clsx(isMobile && 'pk-mr--xs2')}
          showTeamName={!isMobile}
        />
      )}
    </Suspense>
  );
};

export default memo(Identifier);
